/* Join questions — what this church asks everybody who joins.
 *
 * THE CHURCH'S OWN. Until now only whoever set the church up could add one,
 * from the console, and nothing could be changed or taken away afterwards.
 * A church admin — or a role given members.questions — manages them here.
 *
 * WHAT CAN CHANGE. Everything: the wording, the answer type, whether it must
 * be answered, a list's choices, and the order. A new type re-checks the
 * answers already given; the ones that no longer fit are cleared, and the
 * admin is told how many before anything is saved.
 *
 * REMOVING IS RETIRING. The question stops being asked; the answers people
 * gave stay on Members and in audiences chosen by them.
 *
 * Members who joined before a question existed are asked about it on Home.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Loader2,
  Pencil,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field, SelectField } from '@/components/ui/field'
import { api, ApiError } from '@/api/client'
import { useCan, useMe } from '@/api/queries'
import { useChurchFields } from '@/api/console'
import type { CustomField, CustomFieldDraft } from '@/types/api'
import { CustomFieldsEditor } from '@/features/console/CustomFieldsEditor'

const TYPE_LABEL: Record<string, string> = {
  text_box: 'Text box',
  check_box: 'Check box',
  drop_down: 'Drop down (list)',
  date_time: 'Date time',
  image: 'Image',
  number: 'Number',
}

function messageOf(err: unknown, fallback: string) {
  return err instanceof ApiError ? err.message : fallback
}

export function JoinQuestionsPage() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const can = useCan(churchId)

  if (!churchId) return null

  if (!can('members.questions')) {
    return (
      <Card className="p-s6 text-center">
        <p className="font-semibold">Only your church administrator can change these.</p>
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-lg font-extrabold tracking-tight">Join questions</h1>
        <p className="text-base text-ink-2">
          What everybody joining {me?.church_name ?? 'your church'} is asked.
          Members who joined earlier are asked new questions on their Home page.
        </p>
      </header>
      <JoinQuestionsManager churchId={churchId} />
    </div>
  )
}

/** Add, edit, reorder and retire — the whole of it, for one church. Shared by
 *  the church's own page and the console's Signup fields tab, so the two
 *  cannot drift into one that can fix a mistake and one that cannot. */
export function JoinQuestionsManager({ churchId }: { churchId: string }) {
  const fields = useChurchFields(churchId)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [retiring, setRetiring] = useState<CustomField | null>(null)

  function refresh() {
    setError(null)
    return fields.refetch()
  }

  const add = useMutation({
    mutationFn: (f: CustomFieldDraft) =>
      api.post(`/groups/${churchId}/fields`, f),
    onSuccess: refresh,
    onError: (e) => setError(messageOf(e, 'Could not add that question.')),
  })
  const retire = useMutation({
    mutationFn: (id: string) => api.delete(`/groups/${churchId}/fields/${id}`),
    onSuccess: () => {
      setRetiring(null)
      return refresh()
    },
    onError: (e) => setError(messageOf(e, 'Could not remove that question.')),
  })
  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      api.put(`/groups/${churchId}/fields/order`, { field_ids: ids }),
    onSuccess: refresh,
    onError: (e) => setError(messageOf(e, 'Could not move that question.')),
  })

  const list = [...(fields.data ?? [])].sort((a, b) => a.sort_order - b.sort_order)

  function move(i: number, by: -1 | 1) {
    const ids = list.map((f) => f.id)
    const j = i + by
    if (j < 0 || j >= ids.length) return
    ;[ids[i], ids[j]] = [ids[j], ids[i]]
    reorder.mutate(ids)
  }

  return (
    <div className="space-y-s4">
      {error ? (
        <div
          role="alert"
          className="flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      ) : null}

      <Card className="p-s4">
        <CustomFieldsEditor
          fields={list}
          onAdd={(f) => add.mutate(f)}
          busy={add.isPending}
          showList={false}
        />
      </Card>

      {fields.isLoading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : !list.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          No questions yet. People join with their name, mobile and email only.
        </Card>
      ) : (
        <Card className="divide-y divide-line-2">
          {list.map((f, i) =>
            editing === f.id ? (
              <EditRow
                key={f.id}
                churchId={churchId}
                field={f}
                onDone={() => {
                  setEditing(null)
                  refresh()
                }}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <div key={f.id} className="flex items-center gap-s3 px-s4 py-s3">
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold">
                    {f.label}
                    {f.is_required ? (
                      <span className="text-danger" aria-label="required"> *</span>
                    ) : null}
                  </span>
                  <span className="block truncate text-sm text-ink-2">
                    {TYPE_LABEL[f.field_type] ?? f.field_type}
                    {f.options?.length ? ` · ${f.options.join(' · ')}` : ''}
                    {f.is_required ? ' · Must be answered' : ''}
                  </span>
                </span>
                <IconButton
                  label={`Move ${f.label} up`}
                  disabled={i === 0 || reorder.isPending}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  label={`Move ${f.label} down`}
                  disabled={i === list.length - 1 || reorder.isPending}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton label={`Edit ${f.label}`} onClick={() => setEditing(f.id)}>
                  <Pencil className="h-4 w-4" aria-hidden />
                </IconButton>
                <IconButton
                  label={`Remove ${f.label}`}
                  danger
                  disabled={retire.isPending}
                  onClick={() => setRetiring(f)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </IconButton>
              </div>
            ),
          )}
        </Card>
      )}

      <Confirm
        open={retiring !== null}
        onOpenChange={(open) => !open && setRetiring(null)}
        title={`Remove “${retiring?.label ?? ''}”?`}
        body="It stops being asked straight away. Answers people have already given are kept."
        confirmLabel="Remove question"
        loading={retire.isPending}
        onConfirm={() => retiring && retire.mutate(retiring.id)}
      />
    </div>
  )
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={
        'shrink-0 rounded p-1.5 text-ink-2 hover:bg-hover disabled:opacity-40 ' +
        (danger ? 'hover:text-danger' : 'hover:text-ink')
      }
    >
      {children}
    </button>
  )
}

const TYPE_OPTIONS = Object.entries(TYPE_LABEL).map(([value, label]) => ({
  value,
  label,
}))

type Impact = { kept: number; cleared: number }

function EditRow({
  churchId,
  field,
  onDone,
  onCancel,
}: {
  churchId: string
  field: CustomField
  onDone: () => void
  onCancel: () => void
}) {
  const [label, setLabel] = useState(field.label)
  const [type, setType] = useState(field.field_type)
  const [required, setRequired] = useState(field.is_required)
  const [options, setOptions] = useState((field.options ?? []).join(', '))
  const [error, setError] = useState<string | null>(null)
  /* What a change of type would do to answers already given, from a dry run
     — shown before anything is saved. */
  const [impact, setImpact] = useState<Impact | null>(null)

  const typeChanged = type !== field.field_type
  const choices = options
    .split(',')
    .map((o) => o.trim())
    .filter(Boolean)

  function body(dryRun: boolean) {
    return {
      label: label.trim(),
      is_required: required,
      ...(typeChanged ? { field_type: type } : {}),
      ...(type === 'drop_down' ? { options: choices } : {}),
      dry_run: dryRun,
    }
  }

  const save = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.patch<Impact>(`/groups/${churchId}/fields/${field.id}`, body(dryRun)),
    onSuccess: (result, dryRun) => {
      if (dryRun) {
        // Nothing anybody answered is affected: no reason to ask.
        if (result.kept + result.cleared === 0) save.mutate(false)
        else setImpact(result)
        return
      }
      setImpact(null)
      onDone()
    },
    onError: (e) => {
      setImpact(null)
      setError(messageOf(e, 'Could not save that question.'))
    },
  })

  const newType = TYPE_LABEL[type] ?? type

  return (
    <form
      className="space-y-s3 bg-surface-2 px-s4 py-s3"
      onSubmit={(e) => {
        e.preventDefault()
        setError(null)
        save.mutate(typeChanged)
      }}
    >
      <div className="grid gap-s3 sm:grid-cols-2">
        <Field
          label="Question"
          size="sm"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <SelectField
          label="Answer type"
          size="sm"
          value={type}
          onChange={(e) => setType(e.target.value as CustomField['field_type'])}
          options={TYPE_OPTIONS}
        />
      </div>
      {type === 'drop_down' ? (
        <Field
          label="Choices (comma separated)"
          size="sm"
          value={options}
          onChange={(e) => setOptions(e.target.value)}
          help="Answers already given keep their wording if a choice is renamed."
        />
      ) : null}
      <label className="flex items-center gap-s2">
        <input
          type="checkbox"
          checked={required}
          onChange={(e) => setRequired(e.target.checked)}
          className="h-4 w-4 accent-[var(--cn-brand)]"
        />
        <span className="text-base">Must be answered to join</span>
      </label>
      {typeChanged ? (
        <p className="text-sm text-ink-3">
          Answers already given are checked against {newType}. Those that
          still fit are kept; you will see how many cannot be before anything
          is saved.
        </p>
      ) : null}
      {error ? <p className="text-base text-danger">{error}</p> : null}
      <div className="flex justify-end gap-s2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          loading={save.isPending && !impact}
          disabled={!label.trim() || (type === 'drop_down' && !choices.length)}
        >
          Save
        </Button>
      </div>

      <Confirm
        open={impact !== null}
        onOpenChange={(open) => !open && setImpact(null)}
        title={`Change “${field.label}” to ${newType}?`}
        body={
          impact
            ? impact.cleared
              ? `${impact.kept} answer${impact.kept === 1 ? '' : 's'} still fit and are kept. ${impact.cleared} cannot be read as ${newType} and will be cleared.`
              : `All ${impact.kept} answer${impact.kept === 1 ? '' : 's'} given so far still fit and are kept.`
            : null
        }
        confirmLabel="Change type"
        tone={impact?.cleared ? 'danger' : 'primary'}
        loading={save.isPending}
        onConfirm={() => save.mutate(false)}
      />
    </form>
  )
}
